// =============================================================================
// idleSignOut - automatic sign-out after inactivity (HIPAA automatic logoff,
// 45 CFR 164.312(a)(2)(iii)).
//
// Staff and portal sessions used to stay signed in indefinitely. Portal
// patients often use a shared family computer or phone; a clinic workstation
// is left unattended between rooms. After a period with no touch, click, key
// or scroll the user sees a warning with a countdown, then is signed out and
// the sign-in screen says why.
//
// Activity is shared across tabs through localStorage, so reading a chart in
// one tab keeps the whole session alive and "Stay signed in" in any tab
// dismisses the warning everywhere.
//
// Holds. Some work is legitimately hands-off for a long time: an AI Scribe or
// care-conference recording, voice dictation, the waiting-room board, a video
// visit. Those call useIdleHold(true, reason) while active; a held session is
// treated as active and the clock restarts when the hold ends.
// =============================================================================
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "./tokens";
import { useT } from "./i18n";

const ACTIVITY_KEY = "pos-last-activity";
export const SIGNOUT_REASON_KEY = "pos-signout-reason";

// ---- holds -------------------------------------------------------------------
const holds = new Set();

export function holdIdle(reason) {
  const token = { reason };
  holds.add(token);
  return () => { holds.delete(token); };
}

// Hold the session open while `active` is true (a recording, board mode...).
export function useIdleHold(active, reason) {
  useEffect(() => {
    if (!active) return undefined;
    return holdIdle(reason);
  }, [active, reason]);
}

// ---- shared activity clock ---------------------------------------------------------
function readShared() {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
}
function writeShared(t) {
  try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch { /* private mode */ }
}

// The sign-in screen reads this to explain an automatic sign-out. It only
// reads: the sign-in screen can mount more than once while the signed-out
// state settles. clearSignOutReason() runs when the user signs in again.
export function consumeSignOutReason() {
  try { return sessionStorage.getItem(SIGNOUT_REASON_KEY); } catch { return null; }
}
export function clearSignOutReason() {
  try { sessionStorage.removeItem(SIGNOUT_REASON_KEY); } catch { /* ignore */ }
}

// ---- component ---------------------------------------------------------------------
// minutes: total inactivity allowed. warnSeconds: warning shown before the end.
export default function IdleSignOut({ minutes, warnSeconds = 60, onSignOut, audience = "staff" }) {
  // Staff app has no LangProvider, so this is always English there.
  const { t } = useT();
  const lastRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const firedRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(null); // null = no warning

  useEffect(() => {
    const now = Date.now();
    lastRef.current = now;
    writeShared(now);
    const mark = () => {
      const t = Date.now();
      lastRef.current = t;
      // Throttle the shared write; the local ref is exact.
      if (t - lastWriteRef.current > 5000) { lastWriteRef.current = t; writeShared(t); }
    };
    // Input events only. "scroll" is deliberately absent: the app scrolls
    // itself (auto-cycling panels, scroll-into-view), which would keep an
    // unattended session alive forever. User scrolling arrives as wheel,
    // touch or key events.
    const evs = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"];
    evs.forEach((e) => window.addEventListener(e, mark, { capture: true, passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark, { capture: true }));
  }, []);

  useEffect(() => {
    const limitMs = minutes * 60 * 1000;
    const warnMs = warnSeconds * 1000;
    // Timestamps, not tick counts: background tabs throttle timers and a
    // laptop can sleep; on wake an expired session signs out immediately.
    const id = setInterval(() => {
      const now = Date.now();
      if (holds.size > 0) {
        lastRef.current = now;
        if (now - lastWriteRef.current > 5000) { lastWriteRef.current = now; writeShared(now); }
      }
      const last = Math.max(lastRef.current, readShared());
      const idle = now - last;
      if (idle >= limitMs) {
        if (firedRef.current) return;
        firedRef.current = true;
        try { sessionStorage.setItem(SIGNOUT_REASON_KEY, "idle"); } catch { /* ignore */ }
        console.info("[PracticeOS] signing out after " + minutes + " minutes without activity");
        setSecondsLeft(null);
        Promise.resolve(onSignOut && onSignOut()).catch(() => {});
        return;
      }
      if (idle >= limitMs - warnMs) setSecondsLeft(Math.ceil((limitMs - idle) / 1000));
      else setSecondsLeft(null);
    }, 1000);
    return () => clearInterval(id);
  }, [minutes, warnSeconds, onSignOut]);

  const stay = () => {
    const now = Date.now();
    lastRef.current = now;
    lastWriteRef.current = now;
    writeShared(now);
    setSecondsLeft(null);
  };

  if (secondsLeft === null) return null;

  const node = (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-signout-title"
      aria-describedby="idle-signout-body"
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: C.scrim || "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div style={{
        background: C.bgPrimary, color: C.textPrimary, borderRadius: 12,
        padding: "24px 26px", maxWidth: 400, width: "100%",
        boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
      }}>
        <h2 id="idle-signout-title" style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>
          {t("Are you still there?")}
        </h2>
        <p id="idle-signout-body" style={{ margin: "0 0 18px", fontSize: 14, lineHeight: 1.5, color: C.textSecondary }}>
          {(() => {
            // Whole sentence is one key; the bold countdown goes where {n} is.
            const parts = (audience === "patient"
              ? t("To protect your health information, you will be signed out in {n} seconds.")
              : t("To protect patient information, you will be signed out in {n} seconds.")).split("{n}");
            return <>{parts[0]}<strong style={{ color: C.textPrimary }}>{secondsLeft}</strong>{parts.slice(1).join("")}</>;
          })()}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => { try { sessionStorage.setItem(SIGNOUT_REASON_KEY, "manual"); } catch { /* ignore */ } firedRef.current = true; onSignOut && onSignOut(); }}
            style={{
              background: "transparent", color: C.textSecondary, border: "1px solid " + C.borderMid,
              borderRadius: 8, padding: "9px 14px", fontSize: 14, cursor: "pointer", fontFamily: "inherit",
            }}
          >{t("Sign out now")}</button>
          <button
            type="button"
            autoFocus
            onClick={stay}
            style={{
              background: "var(--brand-primary-accessible, var(--brand-primary))", color: C.onAccent,
              border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >{t("Stay signed in")}</button>
        </div>
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
}
